import { ScopeContext, ContextManager, useScope } from 'dreamstate';
import { useLayoutEffect } from 'react';

import { AnyObject } from '@vimeo/core/types/generic';
import { useGctlConfig, GctlConfig } from '@vimeo/gctl-api/hooks/GctlProvider';
import { Viewer } from '@vimeo/viewer';

import { VIMEO_VIEWER_CONTEXT_QUERY } from '../../utils/store/viewer';

export * from '@vimeo/gctl-api/hooks/GctlProvider'; // useGctlConfig / GctlConfig

export const VIMEO_GCTL_CONTEXT_QUERY: string = '@VIMEO/GCTL_CONTEXT_QUERY';

/**
 * Provide current GCTL config to all requesting entities in scope.
 * Consumes it from higher level context and returns within scope below.
 */
export function useScopeGctlProvision(): void {
  const scope: ScopeContext = useScope();
  const config: GctlConfig = useGctlConfig();

  useLayoutEffect(
    () => scope.registerQueryProvider(VIMEO_GCTL_CONTEXT_QUERY, () => config),
    [scope, config],
  );
}

/**
 * Query GCTL config in current manager scope.
 * Shortcut to access GCTL config object in a sync way.
 *
 * @param manager - target manager to query in scope of
 * @returns gctl config in current scope
 */
export function queryManagerGctlConfig(manager: ContextManager<AnyObject, AnyObject>): GctlConfig {
  return manager.queryDataSync({ type: VIMEO_GCTL_CONTEXT_QUERY })?.data;
}

/**
 * Similar query function as `queryManagerGctlConfig`,
 * but with fallback for places where GCTL was not provided for some reason.
 *
 * @param manager - target manager to query in scope of
 * @returns gctl config in current scope or synthetic config object based on viewer
 */
export function queryManagerGctlConfigWithFallback(
  manager: ContextManager<AnyObject, AnyObject>,
): GctlConfig {
  const gctlConfig: GctlConfig = manager.queryDataSync({ type: VIMEO_GCTL_CONTEXT_QUERY })?.data;

  // Fallback attempt to compose inline GCTL config from viewer.
  // Not all vimeo apps are using the same entry-point, some do not provide GCTL config...
  if (Object.keys(gctlConfig).length === 0) {
    const viewer: Viewer = manager.queryDataSync({
      type: VIMEO_VIEWER_CONTEXT_QUERY,
    })?.data;

    if (viewer) {
      return {
        xVimeoPage: '',
        jwt: viewer.jwt,
        baseUrl: `https://${viewer.apiUrl}`,
        locale: viewer.locale,
      };
    }
  }

  return gctlConfig;
}

/**
 * Query GCTL config in current scope.
 *
 * @param scope - target scope to query gctl config
 * @returns gctl config in current scope
 */
export function queryScopeGctlConfig(scope: ScopeContext): GctlConfig {
  return scope.queryDataSync({ type: VIMEO_GCTL_CONTEXT_QUERY })?.data;
}
